/**
 * Branch Context Service
 *
 * Manages the current branch context for the application.
 * This service helps transition from user-centric to branch-centric data access.
 */

import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import type { Branch, BranchMembership } from '../db/schema'

interface BranchContext {
  branchId: string
  branchName: string
  accountId: string
  role: 'owner' | 'member'
}

class BranchContextService {
  private currentBranchContext: BranchContext | null = null
  private userBranches: Branch[] = []
  private db = getFirestore()

  /**
   * Load all branches accessible by the current user
   */
  async loadUserBranches(userId: string): Promise<Branch[]> {
    try {
      // Get all memberships for this user
      const membershipsQuery = query(
        collection(this.db, 'memberships'),
        where('userId', '==', userId),
      )
      const membershipsSnap = await getDocs(membershipsQuery)

      if (membershipsSnap.empty) {
        console.warn('No branch memberships found for user:', userId)
        this.userBranches = []
        return []
      }

      // Get all branch IDs
      const membershipData = membershipsSnap.docs.map((doc) => doc.data() as BranchMembership)
      const branchIds = membershipData.map((m) => m.branchId)

      // Fetch all branches
      const branches: Branch[] = []
      for (const branchId of branchIds) {
        const branchDoc = await getDoc(doc(this.db, 'branches', branchId))
        if (branchDoc.exists()) {
          branches.push({ id: branchDoc.id, ...branchDoc.data() } as Branch)
        }
      }

      this.userBranches = branches

      // Auto-select first branch if none selected
      if (branches.length > 0 && !this.currentBranchContext) {
        const firstMembership = membershipData[0]
        this.setCurrentBranch(branches[0].id, branches[0].branchName, branches[0].accountId, firstMembership.role)
      }

      return branches
    } catch (error) {
      console.error('Error loading user branches:', error)
      return []
    }
  }

  /**
   * Set the current active branch context
   */
  setCurrentBranch(branchId: string, branchName: string, accountId: string, role: 'owner' | 'member') {
    this.currentBranchContext = { branchId, branchName, accountId, role }

    // Store in localStorage for persistence
    localStorage.setItem('currentBranchId', branchId)

    // Dispatch event for listeners
    window.dispatchEvent(new CustomEvent('branchContextChanged', {
      detail: this.currentBranchContext,
    }))
  }

  /**
   * Get the current branch context
   */
  getCurrentBranch(): BranchContext | null {
    return this.currentBranchContext
  }

  /**
   * Get current branch ID (throws if not set)
   */
  requireBranchId(): string {
    if (!this.currentBranchContext) {
      throw new Error('No branch selected. Please select a branch first.')
    }
    return this.currentBranchContext.branchId
  }

  /**
   * Get current account ID (throws if not set)
   */
  requireAccountId(): string {
    if (!this.currentBranchContext) {
      throw new Error('No branch selected. Please select a branch first.')
    }
    return this.currentBranchContext.accountId
  }

  /**
   * Get all branches accessible by user
   */
  getUserBranches(): Branch[] {
    return this.userBranches
  }

  /**
   * Find branch by name (for backward compatibility with legacy code)
   */
  findBranchByName(branchName: string): Branch | undefined {
    return this.userBranches.find((b) => b.branchName === branchName)
  }

  /**
   * Get branch ID by name (for backward compatibility)
   * Returns the branch ID if found, otherwise returns null
   */
  getBranchIdByName(branchName: string): string | null {
    const branch = this.findBranchByName(branchName)
    return branch ? branch.id : null
  }

  /**
   * Clear the current context (for logout)
   */
  clear() {
    this.currentBranchContext = null
    this.userBranches = []
    localStorage.removeItem('currentBranchId')
  }

  /**
   * Update cached branch name when an upload renames a default branch.
   */
  updateBranchName(branchId: string, branchName: string) {
    this.userBranches = this.userBranches.map((branch) => {
      if (branch.id !== branchId) return branch
      return { ...branch, branchName, isDefaultName: false }
    })

    const current = this.currentBranchContext
    if (current?.branchId === branchId) {
      this.setCurrentBranch(branchId, branchName, current.accountId, current.role)
    }
  }

  /**
   * Restore branch context from localStorage
   */
  async restoreBranchContext(userId: string): Promise<boolean> {
    const savedBranchId = localStorage.getItem('currentBranchId')
    if (!savedBranchId) return false

    await this.loadUserBranches(userId)

    const branch = this.userBranches.find((b) => b.id === savedBranchId)
    if (!branch) return false

    // Get membership to determine role
    const membershipQuery = query(
      collection(this.db, 'memberships'),
      where('userId', '==', userId),
      where('branchId', '==', savedBranchId),
    )
    const membershipSnap = await getDocs(membershipQuery)

    if (!membershipSnap.empty) {
      const membership = membershipSnap.docs[0].data() as BranchMembership
      this.setCurrentBranch(branch.id, branch.branchName, branch.accountId, membership.role)
      return true
    }

    return false
  }
}

// Export singleton instance
export const branchContextService = new BranchContextService()
